import { getApi } from "../Services/RestService";

export interface PasswordRules {
  minLength: number;
  maxLength: number;
  requireLetter: boolean;
  requireDigit: boolean;
  requireSpecial: boolean;
  specialCharacters: string;
}

export interface ValidationPolicy {
  emailFormatValidationEnabled: boolean;
  passwordPolicyValidationEnabled: boolean;
  passwordRules: PasswordRules;
}

export const DEFAULT_PASSWORD_RULES: PasswordRules = {
  minLength: 8,
  maxLength: 20,
  requireLetter: true,
  requireDigit: true,
  requireSpecial: true,
  specialCharacters: "!@#$%^&*_-",
};

export const DEFAULT_VALIDATION_POLICY: ValidationPolicy = {
  emailFormatValidationEnabled: true,
  passwordPolicyValidationEnabled: true,
  passwordRules: DEFAULT_PASSWORD_RULES,
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

let cachedPolicy: ValidationPolicy | null = null;
let inFlight: Promise<ValidationPolicy> | null = null;

export const fetchValidationPolicy = (): Promise<ValidationPolicy> => {
  if (cachedPolicy) return Promise.resolve(cachedPolicy);
  if (inFlight) return inFlight;
  inFlight = getApi<ValidationPolicy>("/system-configuration")
    .then((res) => {
      cachedPolicy =
        res.success && res.data
          ? {
              ...DEFAULT_VALIDATION_POLICY,
              ...res.data,
              passwordRules: { ...DEFAULT_PASSWORD_RULES, ...(res.data.passwordRules || {}) },
            }
          : DEFAULT_VALIDATION_POLICY;
      return cachedPolicy;
    })
    .catch(() => {
      cachedPolicy = DEFAULT_VALIDATION_POLICY;
      return cachedPolicy;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

export const invalidateValidationPolicy = () => {
  cachedPolicy = null;
};

export const getEmailValidationError = (
  email?: string | null,
  policy: ValidationPolicy = DEFAULT_VALIDATION_POLICY,
): string | null => {
  if (!email || email.trim().length === 0) return "Email address is required.";
  if (policy.emailFormatValidationEnabled && !EMAIL_REGEX.test(email.trim())) {
    return "Please enter a valid email address.";
  }
  return null;
};

export const getPasswordValidationError = (
  password?: string | null,
  policy: ValidationPolicy = DEFAULT_VALIDATION_POLICY,
): string | null => {
  if (!password || password.length === 0) return "Password is required.";
  if (!policy.passwordPolicyValidationEnabled) return null;
  const rules = policy.passwordRules;
  if (password.length < rules.minLength || password.length > rules.maxLength) {
    return `Password must be ${rules.minLength}-${rules.maxLength} characters long.`;
  }
  if (rules.requireLetter && !/[A-Za-z]/.test(password)) {
    return "Password must contain at least 1 letter.";
  }
  if (rules.requireDigit && !/[0-9]/.test(password)) {
    return "Password must contain at least 1 number.";
  }
  if (rules.requireSpecial && !hasSpecialCharacter(password, rules.specialCharacters)) {
    return `Password must contain at least 1 special character (${rules.specialCharacters}).`;
  }
  return null;
};

const hasSpecialCharacter = (password: string, specialCharacters: string): boolean =>
  password.split("").some((ch) => specialCharacters.includes(ch));

export interface PasswordRuleCheck {
  key: "length" | "letter" | "digit" | "special";
  label: string;
  met: boolean;
}

export const getPasswordRuleChecks = (
  password: string,
  policy: ValidationPolicy = DEFAULT_VALIDATION_POLICY,
): PasswordRuleCheck[] => {
  const rules = policy.passwordRules;
  return [
    {
      key: "length",
      label: `${rules.minLength}-${rules.maxLength} characters`,
      met: password.length >= rules.minLength && password.length <= rules.maxLength,
    },
    { key: "letter", label: "At least 1 letter", met: /[A-Za-z]/.test(password) },
    { key: "digit", label: "At least 1 number", met: /[0-9]/.test(password) },
    {
      key: "special",
      label: `At least 1 special character (${rules.specialCharacters})`,
      met: hasSpecialCharacter(password, rules.specialCharacters),
    },
  ];
};
